/**
 * ProximityDialog – a reusable fullscreen overlay dialog.
 *
 * Usage:
 *   const dlg = new ProximityDialog({
 *     title: "Contact",
 *     bodyHtml: "<p>...</p>",
 *     closeKey: "e",
 *   });
 *   dlg.open();
 *   dlg.close();
 *   dlg.dispose();
 */

export interface ProximityDialogOptions {
  title: string;
  bodyHtml: string;
  /** Keyboard key (lowercase) that closes the dialog, e.g. "e" */
  closeKey?: string;
  /** Label shown on the close button, e.g. "Close  [E]" */
  closeBtnLabel?: string;
}

export class ProximityDialog {
  private overlay: HTMLDivElement;
  private isOpen = false;
  private keyHandler: (e: KeyboardEvent) => void;

  constructor(private options: ProximityDialogOptions) {
    const { title, bodyHtml, closeKey, closeBtnLabel = "Close  [E]" } = options;

    // ── Overlay ──────────────────────────────────────────────────
    this.overlay = document.createElement("div");
    this.overlay.style.cssText = [
      "position:fixed",
      "inset:0",
      "background:rgba(0,0,0,0.6)",
      "display:none",
      "align-items:center",
      "justify-content:center",
      "z-index:9999",
      "font-family:sans-serif",
    ].join(";");

    // ── Panel ────────────────────────────────────────────────────
    const panel = document.createElement("div");
    panel.style.cssText = [
      "background:rgba(10,10,20,0.92)",
      "border:1px solid rgba(255,255,255,0.15)",
      "border-radius:16px",
      "padding:40px 48px",
      "max-width:480px",
      "width:90%",
      "color:#fff",
      "text-align:center",
      "box-shadow:0 8px 40px rgba(0,0,0,0.6)",
    ].join(";");

    // ── Title ────────────────────────────────────────────────────
    const titleEl = document.createElement("h2");
    titleEl.textContent = title;
    titleEl.style.cssText =
      "margin:0 0 12px;font-size:28px;letter-spacing:2px;font-weight:600;";

    // ── Divider ──────────────────────────────────────────────────
    const divider = document.createElement("div");
    divider.style.cssText =
      "width:48px;height:2px;background:rgba(255,180,80,0.8);margin:0 auto 20px;border-radius:2px;";

    // ── Body ─────────────────────────────────────────────────────
    const body = document.createElement("div");
    body.style.cssText =
      "margin:0 0 28px;font-size:15px;color:rgba(255,255,255,0.7);line-height:1.8;";
    body.innerHTML = bodyHtml;

    // ── Close button ─────────────────────────────────────────────
    const closeBtn = document.createElement("button");
    closeBtn.textContent = closeBtnLabel;
    closeBtn.style.cssText = [
      "background:rgba(255,255,255,0.1)",
      "border:1px solid rgba(255,255,255,0.2)",
      "color:#fff",
      "padding:10px 28px",
      "border-radius:8px",
      "font-size:14px",
      "cursor:pointer",
      "letter-spacing:1px",
    ].join(";");

    panel.appendChild(titleEl);
    panel.appendChild(divider);
    panel.appendChild(body);
    panel.appendChild(closeBtn);
    this.overlay.appendChild(panel);
    document.body.appendChild(this.overlay);

    // ── Event listeners ──────────────────────────────────────────
    closeBtn.addEventListener("click", (e) => {
      e.stopPropagation();
      this.close();
    });

    this.overlay.addEventListener("click", (e) => {
      if (e.target === this.overlay) this.close();
    });

    this.keyHandler = (e: KeyboardEvent) => {
      if (closeKey && e.key.toLowerCase() === closeKey && this.isOpen) {
        this.close();
      }
    };
    document.addEventListener("keydown", this.keyHandler);
  }

  get opened(): boolean {
    return this.isOpen;
  }

  open(): void {
    if (this.isOpen) return;
    this.isOpen = true;
    this.overlay.style.display = "flex";
  }

  close(): void {
    if (!this.isOpen) return;
    this.isOpen = false;
    this.overlay.style.display = "none";
  }

  dispose(): void {
    document.removeEventListener("keydown", this.keyHandler);
    this.overlay.remove();
  }
}
